import { useState, useEffect } from 'react';
import { ethers } from 'ethers6';
import { config } from '../utils/config';
import { MAVRK_LENS_ABI } from '../utils/mavrkLensABI';
import { executeWithRpcFailover } from '../utils/rpcProvider';
import { getEthUsdPrice } from '../utils/ethPrice';

// ERC20 ABI for decimals and symbol
const ERC20_ABI = [
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)"
];

// Locker V2 ABI for direct fee queries
const LOCKER_V2_ABI = [
  "function getCreatorUnclaimedFees(uint256 tokenId) external view returns (address token0, address token1, uint256 creatorFees0, uint256 creatorFees1)"
];

// Locker addresses
const LOCKER_V2_ADDRESS = '0x89264dBC5CDc22BbB00ecc672C717D7205647702'; // V2 (Multi-DEX)

// V3 Factory address and ABI
const FACTORY_V3_ADDRESS = '0xE01118dbcF8Ba2609d3ffBD6A53d9533c7526D60';
const FACTORY_V3_ABI = [
  "function nftCreators(uint256 tokenId) view returns (address)",
  "function creatorSplitBps() view returns (uint256)"
];

// NPM addresses for fee queries
const PROTOFIRE_NPM = '0xC0836E5B058BBE22ae2266e1AC488A1A0fD8DCE8';
const INKYSWAP_NPM = '0x1921C0c86494d25F90E9a535327ec989372a860f';

// NPM ABI for positions
const NPM_ABI = [
  "function positions(uint256 tokenId) view returns (uint96 nonce, address operator, address token0, address token1, uint24 fee, int24 tickLower, int24 tickUpper, uint128 liquidity, uint256 feeGrowthInside0LastX128, uint256 feeGrowthInside1LastX128, uint128 tokensOwed0, uint128 tokensOwed1)"
];

// Uniswap V3 Pool ABI
const UNISWAP_V3_POOL_ABI = [
  "function slot0() view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint8 feeProtocol, bool unlocked)",
  "function token0() view returns (address)",
  "function token1() view returns (address)"
];

// Uniswap V3 Factory ABI
const UNISWAP_V3_FACTORY_ABI = [
  "function getPool(address tokenA, address tokenB, uint24 fee) view returns (address pool)"
];

export interface UnclaimedFees {
  tokenId: string;
  token0: string;
  token1: string;
  creatorOwed0: string; // Raw wei amount
  creatorOwed1: string; // Raw wei amount
  usdValue: number; // Calculated USD value
  formatted0: string; // Formatted token0 amount
  formatted1: string; // Formatted token1 amount
  symbol0: string;
  symbol1: string;
}

// Function to calculate price from sqrtPriceX96
function getPriceFromSqrtPriceX96(sqrtPriceX96: bigint, decimals0: number, decimals1: number): number {
  // Price = (sqrtPriceX96 / 2^96)^2
  const Q96 = 2n ** 96n;
  const priceRatio = Number(sqrtPriceX96) / Number(Q96);
  const price = priceRatio * priceRatio;
  
  // Adjust for decimals
  const adjustedPrice = price * (10 ** decimals0) / (10 ** decimals1);
  return adjustedPrice;
}

// Function to get token price from pool
async function getTokenPriceFromPool(
  provider: ethers.Provider,
  tokenAddress: string,
  tokenDecimals: number,
  wethDecimals: number,
  poolAddress: string
): Promise<number | null> {
  try {
    const pool = new ethers.Contract(poolAddress, UNISWAP_V3_POOL_ABI, provider);
    const [token0Address] = await pool.token0();
    const slot0 = await pool.slot0();
    const sqrtPriceX96 = slot0[0];
    
    const isToken0 = tokenAddress.toLowerCase() === token0Address.toLowerCase();
    const price = getPriceFromSqrtPriceX96(sqrtPriceX96, tokenDecimals, wethDecimals);
    
    return isToken0 ? price : 1 / price;
  } catch (err) {
    console.error('[UnclaimedFees] Error getting pool price:', err);
    return null;
  }
}

/**
 * Hook to fetch unclaimed fees for a specific token
 * @param nftTokenId - The NFT token ID
 * @param dex - Optional DEX identifier ('Protofire' or 'InkySwap') for V3 factory tokens
 */
export function useUnclaimedCreatorFees(nftTokenId: string | null | undefined, dex?: string) {
  const [unclaimedFees, setUnclaimedFees] = useState<UnclaimedFees | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!nftTokenId) {
      setUnclaimedFees(null);
      return;
    }

    // Store nftTokenId in a local const to ensure TypeScript knows it's not null
    const tokenId = nftTokenId;

    async function fetchUnclaimedFees() {
      try {
        setIsLoading(true);
        setError(null);

        console.log('[UnclaimedFees] Fetching fees for NFT ID:', tokenId, 'DEX:', dex || 'unknown');

        await executeWithRpcFailover(async (provider) => {
          let feeView: {
            token0: string;
            token1: string;
            creatorOwed0: bigint;
            creatorOwed1: bigint;
          } | null = null;

          // First check if this NFT is in the V3 factory
          let isInV3Factory = false;
          try {
            console.log('[UnclaimedFees] Checking V3 factory...');
            const factoryV3 = new ethers.Contract(
              FACTORY_V3_ADDRESS,
              FACTORY_V3_ABI,
              provider
            );

            const creator = await factoryV3.nftCreators(tokenId);
            if (creator && creator !== '0x0000000000000000000000000000000000000000') {
              console.log('[UnclaimedFees] NFT found in V3 factory, creator:', creator);
              isInV3Factory = true;

              // Determine which NPM to query
              const npmAddress = dex === 'InkySwap' ? INKYSWAP_NPM : PROTOFIRE_NPM;
              console.log('[UnclaimedFees] Using NPM:', npmAddress);

              // Query the NPM for position info
              const npm = new ethers.Contract(npmAddress, NPM_ABI, provider);
              const position = await npm.positions(tokenId);
              
              // position returns: nonce, operator, token0, token1, fee, tickLower, tickUpper, 
              // liquidity, feeGrowthInside0LastX128, feeGrowthInside1LastX128, tokensOwed0, tokensOwed1
              const token0 = position[2];
              const token1 = position[3];
              const tokensOwed0 = position[10];
              const tokensOwed1 = position[11];

              console.log('[UnclaimedFees] NPM position:', {
                token0,
                token1,
                tokensOwed0: tokensOwed0.toString(),
                tokensOwed1: tokensOwed1.toString()
              });

              // Get creator split (default is 25% = 2500 bps)
              let creatorSplitBps = 2500n;
              try {
                creatorSplitBps = await factoryV3.creatorSplitBps();
              } catch {
                console.log('[UnclaimedFees] Using default creator split: 25%');
              }

              // Calculate creator's share (creatorSplitBps / 10000)
              const creatorOwed0 = (BigInt(tokensOwed0) * creatorSplitBps) / 10000n;
              const creatorOwed1 = (BigInt(tokensOwed1) * creatorSplitBps) / 10000n;

              feeView = {
                token0,
                token1,
                creatorOwed0,
                creatorOwed1
              };

              console.log('[UnclaimedFees] V3 factory creator fees:', {
                token0: feeView.token0,
                token1: feeView.token1,
                creatorOwed0: feeView.creatorOwed0.toString(),
                creatorOwed1: feeView.creatorOwed1.toString(),
                creatorSplitBps: creatorSplitBps.toString()
              });
            }
          } catch (v3Error: any) {
            console.log('[UnclaimedFees] V3 factory check failed:', v3Error?.message || v3Error);
            // Not in V3 factory, will try legacy lockers
          }

          // If not in V3, try V2 locker and MavrkLens
          if (!isInV3Factory) {
            // Try V2 locker first (direct call to merged Lens/Locker contract)
            try {
              console.log('[UnclaimedFees] Trying V2 locker direct call...');
              const lockerV2 = new ethers.Contract(
                LOCKER_V2_ADDRESS,
                LOCKER_V2_ABI,
                provider
              );

              const result = await lockerV2.getCreatorUnclaimedFees(tokenId);
              feeView = {
                token0: result[0],
                token1: result[1],
                creatorOwed0: result[2],
                creatorOwed1: result[3]
              };
              
              console.log('[UnclaimedFees] V2 locker result:', {
                token0: feeView.token0,
                token1: feeView.token1,
                creatorOwed0: feeView.creatorOwed0.toString(),
                creatorOwed1: feeView.creatorOwed1.toString()
              });
            } catch (v2Error: any) {
              // V2 failed, fall back to MavrkLens (which checks both old and new lockers)
              const errorMessage = v2Error?.message || v2Error?.toString() || '';
              if (errorMessage.includes('Not locked') || errorMessage.includes('not locked') || errorMessage.includes('not in locker')) {
                console.log('[UnclaimedFees] Token not in V2 locker, trying MavrkLens...');
                
                const mavrkLens = new ethers.Contract(
                  config.ink.mavrkLensAddress,
                  MAVRK_LENS_ABI,
                  provider
                );

                const lensResult = await mavrkLens.creatorFeesForToken(tokenId);
                feeView = {
                  token0: lensResult.token0,
                  token1: lensResult.token1,
                  creatorOwed0: lensResult.creatorOwed0,
                  creatorOwed1: lensResult.creatorOwed1
                };
                
                console.log('[UnclaimedFees] MavrkLens result:', {
                  token0: feeView.token0,
                  token1: feeView.token1,
                  creatorOwed0: feeView.creatorOwed0.toString(),
                  creatorOwed1: feeView.creatorOwed1.toString()
                });
              } else {
                throw v2Error;
              }
            }
          }

          // If feeView is still null, we couldn't find the NFT in any contract
          if (!feeView) {
            throw new Error('NFT not found in V3 factory, V2 locker, or MavrkLens');
          }

          // Check if there are any fees
          if (feeView.creatorOwed0 === 0n && feeView.creatorOwed1 === 0n) {
            console.log('[UnclaimedFees] No unclaimed fees for this NFT');
            setUnclaimedFees({
              tokenId: tokenId,
              token0: feeView.token0,
              token1: feeView.token1,
              creatorOwed0: '0',
              creatorOwed1: '0',
              usdValue: 0,
              formatted0: '0',
              formatted1: '0',
              symbol0: '',
              symbol1: ''
            });
            return;
          }

          // Get decimals and symbols for both tokens
          const token0Contract = new ethers.Contract(feeView.token0, ERC20_ABI, provider);
          const token1Contract = new ethers.Contract(feeView.token1, ERC20_ABI, provider);
          
          const [decimals0, symbol0, decimals1, symbol1] = await Promise.all([
            token0Contract.decimals(),
            token0Contract.symbol().catch(() => '???'),
            token1Contract.decimals(),
            token1Contract.symbol().catch(() => '???')
          ]);

          // Format amounts
          const formatted0 = ethers.formatUnits(feeView.creatorOwed0, decimals0);
          const formatted1 = ethers.formatUnits(feeView.creatorOwed1, decimals1);

          // Calculate USD value
          const ethPrice = await getEthUsdPrice();
          let usdValue = 0;

          // If token0 or token1 is WETH, calculate USD value
          const WETH_ADDRESS = '0x4200000000000000000000000000000000000006';
          if (feeView.token0.toLowerCase() === WETH_ADDRESS.toLowerCase()) {
            usdValue += parseFloat(formatted0) * ethPrice;
          }
          if (feeView.token1.toLowerCase() === WETH_ADDRESS.toLowerCase()) {
            usdValue += parseFloat(formatted1) * ethPrice;
          }

          const fees: UnclaimedFees = {
            tokenId: tokenId,
            token0: feeView.token0,
            token1: feeView.token1,
            creatorOwed0: feeView.creatorOwed0.toString(),
            creatorOwed1: feeView.creatorOwed1.toString(),
            usdValue,
            formatted0,
            formatted1,
            symbol0,
            symbol1
          };

          console.log('[UnclaimedFees] Calculated fees:', fees);

          setUnclaimedFees(fees);
        });
      } catch (err) {
        console.error('[UnclaimedFees] Error fetching fees:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setUnclaimedFees(null);
      } finally {
        setIsLoading(false);
      }
    }

    fetchUnclaimedFees();
  }, [nftTokenId, dex]);

  return { unclaimedFees, isLoading, error };
}

